/**
 * Tunable values for the core simulation (spec §77, trimmed to what pure
 * logic needs — audio levels, parallax multipliers etc. belong in the
 * rendering layer, not here). Widths/heights are abstract units, not
 * pixels — the canvas layer picks its own units-per-pixel scale at draw
 * time, mirroring how game-core works in real milliseconds rather than
 * anything render-specific.
 */
export interface StackTowerConfig {
  /** Total horizontal span the moving block can travel across. */
  readonly trackWidth: number;
  /** Width of block #1, before any cut. */
  readonly startingBlockWidth: number;
  /** Abstract vertical unit per block — height/altitude flavour text derives from this. */
  readonly blockHeight: number;
  /**
   * CONSTANT horizontal speed. Never scales with score — the entire design
   * principle (spec §1) is that difficulty comes from the player's own
   * shrinking margin, not the game getting faster.
   */
  readonly moveSpeedUnitsPerSec: number;
  /** Distance from the previous block's exact centre that still counts as PERFECT (spec §3), strictly less-than. */
  readonly perfectToleranceUnits: number;
  /**
   * Score-per-block steps up by +1× every this many blocks placed in the
   * current unbroken climb (block 1 through this value scores ×1, the next
   * block scores ×2, and so on) — see `multiplierForClimb`. Without this, a
   * flat 1-point-per-block score makes height worthless past the first
   * block: in the timed head-to-head mode, where a miss resets the tower but
   * keeps the score (spec §103), a thin top-block margin is pure downside
   * with no extra payoff, so the rational move is to deliberately miss and
   * farm the wide, easy starting footprint instead of pushing higher. Tying
   * more points to height, and resetting the multiplier along with the
   * tower on a miss, makes climbing pay and makes losing height genuinely
   * costly instead of free.
   */
  readonly scoreMultiplierBlocksPerTier: number;
}

/**
 * Placeholder starting values (roadmap.md / docs/stack_tower_implementation_plan.md
 * flag these as needing in-browser playtesting, not a paper answer — the
 * spec itself is only qualitative here: "should already feel slightly
 * tense"). Safe to retune without touching any other module; nothing else
 * hardcodes these numbers.
 */
export const DEFAULT_STACK_TOWER_CONFIG: StackTowerConfig = {
  trackWidth: 1000,
  startingBlockWidth: 380,
  blockHeight: 40,
  moveSpeedUnitsPerSec: 520,
  perfectToleranceUnits: 10,
  scoreMultiplierBlocksPerTier: 20,
};
